'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/authContext';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, CheckCircle } from 'lucide-react';

const perks = [
  'Personalised college shortlist based on your scores',
  'Track applications & deadlines in one place',
  'AI-powered admission probability calculator',
  'Direct chat with verified college counsellors',
];

export default function SignUpPage() {
  const { signUp } = useAuth();
  const router = useRouter();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    const res = await signUp(name, email, password, 'student');
    setLoading(false);
    if (res.error) { setError(res.error); return; }

    // Send welcome email via Brevo (non-blocking)
    fetch('/api/email/send-welcome', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email }),
    }).catch(() => {/* silent fail — email is best-effort */});

    router.push('/student-profile');
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] bg-secondary p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-primary blur-3xl" />
          <div className="absolute bottom-10 left-10 w-64 h-64 rounded-full bg-accent blur-3xl" />
        </div>
        <Link href="/home" className="flex items-center gap-2 relative z-10">
          <AppLogo size={40} />
          <span className="font-extrabold text-2xl text-white">Edu<span style={{color:'#A78BFA'}}>Portal</span></span>
        </Link>
        <div className="relative z-10">
          <h2 className="text-4xl font-extrabold text-white leading-tight mb-4">
            Start Your<br />College Journey<br />Today
          </h2>
          <p className="text-white/70 text-lg mb-8">Join 2.4 million students who found their dream college on All About Campus.</p>
          <div className="space-y-4">
            {perks.map(p => (
              <div key={p} className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 shrink-0 mt-0.5" />
                <span className="text-white/80 text-sm">{p}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-white/40 text-xs relative z-10">© 2026 All About Campus. All rights reserved.</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link href="/home" className="lg:hidden flex items-center gap-2 mb-8">
            <AppLogo size={32} />
            <span className="font-extrabold text-xl text-secondary">Edu<span className="text-primary">Portal</span></span>
          </Link>

          <div className="mb-8">
            <h1 className="text-3xl font-extrabold text-foreground mb-2">Create your account</h1>
            <p className="text-muted-foreground">Free forever. No credit card required.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Full name</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="text" value={name} onChange={e => setName(e.target.value)} required
                  placeholder="Arjun Sharma"
                  className="input-base pl-9" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Email address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  placeholder="you@example.com"
                  className="input-base pl-9" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                  placeholder="Min. 6 characters"
                  className="input-base pl-9 pr-10" />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{error}</div>
            )}

            <p className="text-xs text-muted-foreground">
              By signing up, you agree to our{' '}
              <span className="text-primary font-semibold cursor-pointer hover:underline">Terms of Service</span> and{' '}
              <span className="text-primary font-semibold cursor-pointer hover:underline">Privacy Policy</span>.
            </p>

            <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-base">
              {loading ? (
                <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creating account...</span>
              ) : (
                <span className="flex items-center gap-2">Create Free Account <ArrowRight size={16} /></span>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link href="/sign-in" className="text-primary font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
